// @ts-ignore
import React from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import WplButton from "@/components/wpl/button/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { getReportsAndInsightsSectionImage } from "@/templates/jira-software-features-page/src/localized-images";

const containerStyle = css({
    display: "flex",
    flexFlow: "column",
    alignItems: "center",
    alignSelf: "center",
    textAlign: "center",

    [media.below.md]: {
        padding: "16px 0"
    },
    [media.above.md]: {
        padding: "64px 0"
    }
});

const headers = css({
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "1rem",

    margin: "0 auto",
    [media.below.sm]: {
        width: "100%"
    },
    [media.above.sm]: {
        width: "66.66%"
    }
});

const h6Styles = css({
    color: "#42526e",
    fontSize: ".75rem",
    letterSpacing: "1px",
    lineHeight: "2",
    marginBlockStart: "0",

    [media.below.md]: {
        marginBlockEnd: "10px"
    },
    [media.above.md]: {
        marginBlockEnd: "16px"
    },
    textTransform: "uppercase",
    fontWeight: "400"
});

const h2Styles = css({
    "display": "block",
    "textAlign": "center",
    "color": "#253858",
    "letterSpacing": "0",
    "marginBlockStart": "0",
    "fontWeight": "500",
    "verticalAlign": "middle",

    [media.below.md]: {
        marginBlockEnd: "14px"
    },
    [media.above.md]: {
        marginBlockEnd: "24px"
    },

    "@media (min-width: 768px)": {
        fontSize: "2.25rem",
        lineHeight: "1.33333333"
    },
    "& span": { verticalAlign: "middle" }
});

const pStyle = css({
    fontSize: "1rem",
    textAlign: "center",
    justifyContent: "center",
    color: "rgb(9, 30, 66)",
    marginBlockEnd: "24px",
    marginBlockStart: "0"
});

const dropShadowStyle = css({
    padding: "16px",
    filter: "drop-shadow(2px 4px 6px rgba(0, 0, 0, 0.3))",
    margin: "0 auto",
    width: "66.6%",
    [media.below.sm]: {
        display: "block",
        width: "100%",
        height: "auto",
        borderRadius: "10px"
    }
});

const StyledImage = () => {
    const intl = useIntl();
    const localizedImageSrc = getReportsAndInsightsSectionImage(intl.locale);

    return (
        <div>
            <img
                css={dropShadowStyle}
                alt={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageTemplate.reports-and-insights.imgAlt",
                    defaultMessage: "Insights example in Jira Software"
                })}
                src={localizedImageSrc}
                loading={"lazy"}
            />
        </div>
    );
};

const StyledAboveTextImage = () => {
    const imageBlockStyles = css({
        [media.above.sm]: {
            display: "none"
        }
    });

    return (
        <div css={imageBlockStyles}>
            <StyledImage />
        </div>
    );
};

const StyledBelowTextImage = () => {
    const imageBlockStyles = css({
        [media.below.sm]: {
            display: "none"
        }
    });

    return (
        <div css={imageBlockStyles}>
            <StyledImage />
        </div>
    );
};

const JSWFeaturesPageReportsAndInsights = () => {
    const intl = useIntl();

    return (
        <div css={containerStyle}>
            <StyledAboveTextImage />
            <div css={headers}>
                <h6 css={h6Styles}>
                    {intl.formatMessage({
                        id: "JiraSoftwareFeaturesPageTemplate.reports-and-insights.subtitle",
                        defaultMessage: "Learn and improve"
                    })}
                </h6>

                <h2 css={h2Styles}>
                    {intl.formatMessage({
                        id: "JiraSoftwareFeaturesPageTemplate.reports-and-insights.title",
                        defaultMessage: "Reports and insights"
                    })}
                </h2>
                <div>
                    <p css={pStyle}>
                        {intl.formatMessage({
                            id: "JiraSoftwareFeaturesPageTemplate.reports-and-insights.description",
                            defaultMessage:
                                "Out-of-the-box reports and dashboards in Jira Software offer critical insights within the context of your work to ensure your teams are always up to date and set up for success."
                        })}
                    </p>
                </div>

                <div>
                    <WplButton
                        text={intl.formatMessage({
                            id: "JiraSoftwareFeaturesPageTemplate.reports-and-insights.learnMore",
                            defaultMessage: "Learn more"
                        })}
                        url={useLocalizedHref(
                            "/software/jira/features/reports"
                        )}
                        size={"small"}
                        type={"secondary"}
                        css={css({
                            display: "inline-flex",
                            fontWeight: "500",
                            border: "1px solid #0052cc"
                        })}
                        dataEventProps={{
                            "data-product-key": "jiraSoftwareFeature",
                            "data-event": "clicked",
                            "data-event-component": "linkButton",
                            "data-event-container": "reportsAndInsightsSection"
                        }}
                    />
                </div>
            </div>
            <StyledBelowTextImage />
        </div>
    );
};

export default JSWFeaturesPageReportsAndInsights;
